import { useEffect, useState } from 'react';
import { useListState } from '@mantine/hooks';
import { Contact, ContactS } from '../ContactS';

export const useContact = (contactId: number | null | string | undefined) => {
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState<Contact | null>(null);

  useEffect(() => {
    if (contactId) {
      setLoading(true);
      ContactS.fetchById(Number(contactId))
        .then(setContact)
        .finally(() => setLoading(false));
    }
  }, [contactId]);

  const editContact = async (patch: Partial<Contact>) => {
    if (contact) {
      const resJSON = await ContactS.edit(contact.id, patch);
      if (resJSON) {
        setContact(resJSON);
        return true;
      }
    }
    return false;
  };

  return { loading, contact, setContact, editContact };
};

export const useContacts = (contactIds: number[]) => {
  const [loading, setLoading] = useState(false);
  const [contacts, handlers] = useListState<Contact>([]);
  const { setState, applyWhere } = handlers;

  useEffect(() => {
    if (contactIds.length > 0) {
      setLoading(true);
      Promise.all(contactIds.map((contactId) => ContactS.fetchById(contactId)))
        .then(async (values) => {
          const fetchedContacts = values as Contact[];
          const existingContactIds = new Set(fetchedContacts.map((contact) => contact.id));
          const missingPartnerIds = Array.from(
            new Set(
              fetchedContacts
                .map((contact) => contact.partnerId)
                .filter((partnerId): partnerId is number => !!partnerId && !existingContactIds.has(partnerId)),
            ),
          );

          if (missingPartnerIds.length === 0) {
            setState(fetchedContacts);
            return;
          }

          const partnerContacts = (await Promise.all(
            missingPartnerIds.map((partnerId) => ContactS.fetchById(partnerId)),
          )) as Contact[];

          setState([...fetchedContacts, ...partnerContacts]);
        })
        .finally(() => setLoading(false));
    }
  }, [contactIds, setState]);

  const update = (contact: Contact) => {
    applyWhere(
      (c) => c.id === contact.id,
      () => contact,
    );
  };
  return { loading, contacts, update };
};
